import type { en } from './en.js';

type TranslationShape<T> = {
  readonly [Key in keyof T]: T[Key] extends object ? TranslationShape<T[Key]> : string;
};

export const uk: TranslationShape<typeof en> = {
  common: {
    loading: 'Завантаження…',
    language: 'Мова',
    english: 'English',
    ukrainian: 'Українська',
    select: 'Виберіть…',
    search: 'Пошук…',
    noMatches: 'Нічого не знайдено.',
    cancel: 'Скасувати',
    close: 'Закрити',
    save: 'Зберегти',
    delete: 'Видалити',
    edit: 'Редагувати',
    back: 'Назад',
    download: 'Завантажити',
    copied: 'Скопійовано',
    yes: 'Так',
    no: 'Ні',
  },
  auth: {
    productName: 'Генератор статей',
    signInToContinue: 'Увійдіть, щоб продовжити',
    welcomeBack: 'З поверненням',
    email: 'Електронна пошта',
    password: 'Пароль',
    signingIn: 'Вхід…',
    signIn: 'Увійти',
  },
  roles: {
    admin: 'Адміністратор',
    user: 'Користувач',
  },
  nav: {
    dashboard: 'Головна',
    jobs: 'Задачі',
    templates: 'Шаблони',
    aiKeys: 'AI-ключі',
    legacyImport: 'Імпорт legacy',
    settings: 'Налаштування',
    allJobs: 'Усі задачі',
    sharedTokens: 'Спільні токени',
    users: 'Користувачі',
    proxy: 'Проксі',
    costs: 'Витрати',
    admin: 'Адміністрування',
    closeMenu: 'Закрити меню',
    openMenu: 'Відкрити меню',
    signOut: 'Вийти',
    contact: 'Запитання? Напишіть',
  },
  theme: {
    switchToLight: 'Увімкнути світлу тему',
    switchToDark: 'Увімкнути темну тему',
    toggle: 'Змінити тему',
  },
  status: {
    draft: 'Чернетка',
    queued: 'У черзі',
    running: 'Виконується',
    paused: 'Призупинено',
    succeeded: 'Успішно',
    failed: 'Помилка',
    cancelled: 'Скасовано',
    pending: 'Очікує',
    generating: 'Генерується',
    validating: 'Перевіряється',
    inserting: 'Вставляється',
    exporting: 'Експортується',
    regenerating: 'Повторно генерується',
    done: 'Готово',
    review: 'Перевірка',
    needsReview: 'Потрібна перевірка',
  },
  support: {
    settings: 'Налаштування',
    settingsDescription: 'Налаштуйте провайдерів, значення за замовчуванням та інтеграції.',
    aiKeysTitle: 'AI-ключі',
    aiKeysDescription: 'Керуйте зашифрованими ключами провайдерів.',
    openAiKeys: 'Відкрити AI-ключі',
    templatesTitle: 'Шаблони',
    templatesDescription: 'Редагуйте набори промптів для задач.',
    openTemplates: 'Відкрити шаблони',
    notFound: 'Сторінку не знайдено',
    notFoundDescription: 'Сторінка не існує або була переміщена.',
    backToDashboard: 'На головну',
    legacyImport: 'Імпорт legacy',
    legacyImportDescription: 'Вставте JSON legacy-задачі, щоб нормалізувати та створити нову задачу.',
    legacyJson: 'Legacy JSON',
    previewDiff: 'Попередній перегляд змін',
    createJob: 'Створити задачу',
    diff: 'Зміни',
    noTransformations: 'Перетворення не потрібні.',
    warningsAndUnknowns: 'Попередження та невідомі поля',
    none: 'Немає.',
    unknownFieldsIgnored: 'Невідомі поля проігноровано: {{fields}}',
    invalidJson: 'Некоректний JSON',
    previewFailed: 'Не вдалося отримати попередній перегляд',
    commitFailed: 'Не вдалося створити задачу',
    jobCreatedFromImport: 'Задачу створено з legacy-імпорту',
  },
  dashboard: {
    title: 'Головна',
    description: 'Огляд останніх задач і швидкі дії.',
    totalJobs: 'Усього задач',
    readyJobs: 'Готові задачі',
    apiStatus: 'Стан API',
    online: 'Онлайн',
    offline: 'Недоступний',
    recentJobs: 'Останні задачі',
    viewAll: 'Переглянути всі',
    noJobs: 'Задач ще немає',
    noJobsDescription: 'Створіть першу задачу пакетної генерації, щоб почати.',
    articles: '{{count}} статей',
    open: 'Відкрити',
    spend: 'Витрати',
    spendToday: 'Сьогодні',
    spendWeek: 'Останні 7 днів',
    spendMonth: 'Цей місяць',
    spendHint: 'Лише OpenRouter — інші провайдери вартість не повертають', spendUnavailable: 'Витрати недоступні — не вдалося звернутися до API',
    tasks: 'Запущені таски',
    tasksHint: 'Видалення чи перезапуск таски лічильник не зменшує',
  },
  adminCosts: {
    title: 'Витрати',
    description: 'Витрати кожного користувача за даними OpenRouter і кількість запущених тасок.',
    user: 'Користувач',
    spend: 'Витрати',
    tasks: 'Запущені таски',
    today: 'Сьогодні',
    week: 'Останні 7 днів',
    month: 'Цей місяць',
    total: 'Усі разом',
    deletedAccounts: 'Видалені акаунти',
    empty: 'Витрат ще немає.', failed: 'Не вдалося завантажити витрати.',
  },
  jobs: {
    title: 'Задачі',
    description: 'Переглядайте, запускайте та редагуйте задачі пакетної генерації.',
    newJob: 'Нова задача',
    createJob: 'Створити задачу',
    newJobDescription: 'Налаштуйте нову задачу пакетної генерації.',
    filterPlaceholder: 'Фільтр за назвою, брендом, регіоном…',
    noMatches: 'Задач не знайдено',
    noMatchesDescription: 'Спробуйте інший запит.',
    noJobs: 'Задач ще немає',
    noJobsDescription: 'Створіть першу задачу, щоб почати генерацію статей.',
    name: 'Назва',
    brand: 'Бренд',
    region: 'Регіон',
    articles: 'Статті',
    status: 'Статус',
    actions: 'Дії',
    run: 'Запустити',
    runStarted: 'Запуск розпочато',
    runFailed: 'Не вдалося запустити',
    deleteJob: 'Видалити задачу "{{name}}"?',
    jobDeleted: 'Задачу видалено',
    deleteFailed: 'Не вдалося видалити',
    jobCreated: 'Задачу створено',
    saveFailed: 'Не вдалося зберегти',
  },
  templates: {
    title: 'Шаблони',
    description: 'Повторно використовувані набори промптів для задач.',
    newTemplate: 'Новий шаблон',
    noTemplates: 'Шаблонів ще немає.',
    name: 'Назва',
    systemPrompts: 'Системні промпти',
    version: 'Версія',
    updated: 'Оновлено',
    titlePrompt: 'Заголовок',
    articlePrompt: 'Стаття',
    custom: 'власний',
    default: 'стандартний',
    deleteTemplate: 'Видалити шаблон "{{name}}"?',
    templateDeleted: 'Шаблон видалено',
    templateSaved: 'Шаблон збережено',
    newTemplateTitle: 'Новий шаблон',
    editTemplateTitle: 'Редагувати шаблон',
    template: 'Шаблон',
    titleGeneration: 'Генерація заголовка',
    titleGenerationDescription: 'Промпти для генерації заголовка статті.',
    articleGeneration: 'Генерація статті',
    articleGenerationDescription: 'Промпти для генерації основного тексту статті.',
    customSystemPrompt: 'Власний системний промпт',
    builtInSystemPrompt: 'Якщо вимкнено, використовується вбудований системний промпт.',
    userPrompt: 'Користувацький промпт',
    macros: 'Макроси: {{macros}}',
    saving: 'Збереження…',
  },
  aiKeys: {
    title: 'AI-ключі',
    description: 'Ключі провайдерів зашифровані AES-256-GCM. Відкритий ключ ніколи не повертається.',
    addKey: 'Додати ключ',
    noKeys: 'Ключів ще немає.',
    lastTest: 'Остання перевірка:',
    never: 'ніколи',
    test: 'Перевірити',
    keyAdded: 'Ключ додано',
    keyDeleted: 'Ключ видалено',
    keyUpdated: 'Ключ оновлено',
    updateFailed: 'Не вдалося оновити',
    noKeySelected: 'Ключ не вибрано',
    connectionOk: 'З’єднання успішне · {{latency}} мс',
    connectionFailed: 'Не вдалося підключитися',
    deleteKey: 'Видалити ключ "{{label}}"?',
    addTitle: 'Додати AI-ключ',
    addDescription: 'Ключ шифрується master key сервера. Відкритий ключ використовується лише в пам’яті воркера.',
    editTitle: 'Редагувати AI-ключ',
    editDescription: 'Залиште поле API-ключа порожнім, щоб зберегти поточний ключ.',
    label: 'Мітка',
    provider: 'Провайдер',
    apiKey: 'API-ключ',
    newApiKey: 'Новий API-ключ',
    optional: 'необов’язково',
    keepCurrentKey: 'Залиште порожнім, щоб зберегти поточний ключ',
  },
  jobForm: {
    settings: 'Налаштування', inserts: 'Вставки', general: 'Загальне', jobName: 'Назва задачі', brand: 'Бренд', domain: 'Домен', region: 'Регіон', language: 'Мова', selectRegion: 'Виберіть регіон…', selectLanguage: 'Виберіть мову…',
    pageInputs: 'Вхідні дані сторінки (ключові слова)', keywords: 'Ключові слова (по одному в рядку)', articlesToGenerate: 'Статей для генерації', combination: 'Поєднання', oneKeyword: 'Одне ключове слово на статтю', allKeywords: 'Усі ключові слова', random: 'Випадково', availableMacros: 'Доступні макроси: {{macros}}',
    aiWriter: 'AI-автор', provider: 'Провайдер', model: 'Модель', apiKey: 'API-ключ', selectKey: 'Виберіть ключ', noKeys: 'Ключів немає — додайте їх у Налаштування → AI-ключі', sharedPool: 'Використовується спільний пул OpenRouter — введіть назву моделі вище.', sendTemperature: 'Передавати temperature', temperature: 'Temperature', maxTokens: 'Макс. токенів', timeout: 'Таймаут (мс)', maxPriceInput: 'Макс. ціна, вхід ($ за 1M токенів)', maxPriceOutput: 'Макс. ціна, вихід ($ за 1M токенів)', maxPriceHint: 'OpenRouter ніколи не візьме провайдера, дорожчого за цю ціну. Якщо жоден не підходить, стаття впаде. Порожньо або 0 — без обмеження.', maxRunCost: 'Макс. вартість таски ($)', maxRunCostHint: 'Коли ран витратить стільки, він стане на паузу (статті в роботі допишуться). Щоб продовжити, підніміть ліміт у налаштуваннях рана. Порожньо або 0 — без обмеження.', outputFormat: 'Формат виводу', htmlLegacy: 'HTML (legacy)', jsonRecommended: 'JSON (рекомендовано для OpenAI)',
    template: 'Шаблон', promptTemplate: 'Шаблон промптів', defaultTemplate: 'Стандартний (вбудовані промпти)', builtInPrompts: 'Для системних і користувацьких промптів використовуються вбудовані значення.',
    export: 'Експорт', fileExtension: 'Розширення файлу', filenameFormat: 'Формат назви файлу', runtime: 'Виконання', concurrency: 'Паралельність', retryCount: 'Кількість повторів', minWordCount: 'Мін. кількість слів', maxWordCount: 'Макс. кількість слів', minParagraphs: 'Мін. кількість абзаців', titleMaxTokens: 'Макс. токенів заголовка', titleMinLength: 'Мін. довжина заголовка', titleMaxLength: 'Макс. довжина заголовка', titleMaxAttempts: 'Макс. спроб заголовка', scheduleCron: 'Cron-розклад (необов’язково)', saving: 'Збереження…',
  },
  jobEdit: {
    editJob: 'Редагувати задачу', saveAndRun: 'Зберегти й запустити', running: 'Запуск…', saveChanges: 'Зберегти зміни', jobSaved: 'Задачу збережено', recentTasks: 'Останні задачі', searchTask: 'Пошук задачі…', deleteSelected: 'Видалити вибрані: {{count}}', perPage: 'На сторінці', noTasksMatch: 'Немає задач за вашим запитом.', noTasks: 'Задач ще немає.', task: 'Задача', doneFailedTotal: 'Готово / Помилки / Усього', started: 'Розпочато', cost: 'Вартість', deletingIn: 'Видалення через {{count}} с…', restartingIn: 'Перезапуск через {{count}} с…', undo: 'Скасувати', open: 'Відкрити', pauseRun: 'Призупинити запуск', resumeRun: 'Продовжити запуск', restartRun: 'Перезапустити запуск', downloadZip: 'Завантажити ZIP', deleteRun: 'Видалити запуск', showing: 'Показано {{from}}–{{to}} з {{total}}', previous: 'Попередня', next: 'Наступна', pageOf: 'Сторінка {{page}} з {{total}}', deleteTasks: 'Видалити {{count}} задачу?', deleteTasks_plural: 'Видалити {{count}} задач?', activeRunsWarning: 'Активні запуски буде автоматично скасовано. Цю дію не можна скасувати.', deleting: 'Видалення…', pauseFailed: 'Не вдалося призупинити', resumeFailed: 'Не вдалося продовжити', restartFailed: 'Не вдалося перезапустити', taskRestarted: 'Задачу перезапущено',
  },
  deploy: {
    button: 'Деплой', deploying: 'Деплой…', deployed: 'Задеплоєно', deployedAt: 'Задеплоєно {{date}}', campaign: 'Кампанія {{id}}', rowHint: 'Задеплоєно в Checker',
    connectTitle: 'Підключити Checker', connectDescription: 'Увійдіть у свій акаунт Checker. Дані зберігаються зашифровано і використовуються лише для деплою тасок.',
    credentialsStale: 'Збережений логін Checker перестав працювати — увійдіть ще раз, щоб продовжити.',
    nameTaken: 'Кампанія з такою назвою вже існує в Checker. Вкажіть іншу назву і задеплойте ще раз.',
    nameLabel: 'Назва кампанії',
    retryWithName: 'Задеплоїти з цією назвою',
    login: 'Логін', password: 'Пароль', connect: 'Підключити', connecting: 'Підключення…', connectedAs: 'Підключено як {{login}}', disconnect: 'Відключити',
  },
  runDetail: {
    jobs: 'Задачі', job: 'Задача', run: 'Запуск', pause: 'Призупинити', pinPause: 'Зафіксувати паузу', pinPauseHint: 'Зафіксувати паузу, щоб автовідновлення не запустило цей запуск знову', resume: 'Продовжити', cancel: 'Скасувати', restart: 'Перезапустити', restartIn: 'Перезапуск через {{count}} с…', downloadZip: 'Завантажити ZIP', progress: 'Прогрес', done: 'готово', failed: 'помилка', total: 'усього', live: 'Наживо', started: 'Розпочато', duration: 'Тривалість', tokensInOut: 'Токени: вхід / вихід', cachedTokens: 'Кешовані токени', cost: 'Вартість', costHint: 'За даними OpenRouter', taskSettings: 'Налаштування задачі', modified: 'Змінено', articles: 'Статті', search: 'Пошук ключового слова чи заголовка…', allStatuses: 'Усі статуси', review: 'Перевірка', keyword: 'Ключове слово', title: 'Заголовок', words: 'Слова', file: 'Файл', noMatches: 'Нічого не знайдено', noMatchesDescription: 'Немає статей за вибраними фільтрами.', noArticles: 'Статей ще немає', noArticlesDescription: 'Статті з’являться тут після початку генерації.', perPage: 'На сторінці', deleteSelected: 'Видалити вибрані: {{count}}', retrySelected: 'Повторити вибрані: {{count}}', retryFailed: 'Повторити помилкові: {{count}}', regenerateReview: 'Повторно згенерувати на перевірці: {{count}}', queuing: 'Додавання в чергу…', reSearchVideo: 'Шукати відео знову', regenerate: 'Перегенерувати', retry: 'Повторити', deleteArticle: 'Видалити статтю', deletingIn: 'Видалення через {{count}} с…', undo: 'Скасувати', showing: 'Показано {{from}}–{{to}} з {{total}}', previous: 'Попередня', next: 'Наступна', pageOf: 'Сторінка {{page}} з {{total}}', deleteArticles: 'Видалити статті: {{count}}?', deleteArticlesDescription: 'Вибрані статті буде назавжди видалено, а лічильники запуску оновлено. Цю дію не можна скасувати.', deleting: 'Видалення…', regenerateReviewTitle: 'Повторно згенерувати статті на перевірці: {{count}}?', regenerateReviewDescription: 'Кожну статтю, позначену для перевірки, буде згенеровано з нуля: поточний вміст заміниться, а позначку перевірки буде знято.', liveLog: 'Журнал у реальному часі', awaitingEvents: 'Очікування подій…', noEvents: 'Подій немає.', reviewIframe: 'Перевірити iframe', reviewIframeHint: 'Відео не вставлено — додайте iframe вручну',
  },
};
